import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import translate from '../../../commonComponents/translate';
import { formatPrice } from '../../../services/utilityFunctions';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import AddressProp from '../../../commonComponents/propTypes/addressProp';
import OrderPriceProp from '../propTypes/orderPriceProp';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import ShippingProviderProp from '../propTypes/shippingProviderProp';
import User from '../../../../../../domain/models/user';
import { normalizeNumber } from '../../../services/numberUtilis';

/** Currently we are requesting the API to send the order details
 * /api/market/order/calculate
 */
function ShopPriceEstimation({
  t,
  cartState,
  frequency,
  orderDetails,
  onClearCoupon,
  onClearCoins,
  showClearCoupon,
  showClearCoins,
  directSellingActive,
}) {
  const { tenant } = useContext(ConfigsContext);
  const { directSellingSettings } = tenant?.settings || {};
  const { CVIntegration, VAIntegration } = directSellingSettings || {};
  const { loggedUser } = useContext(LoggedUserContext);
  const showVA =
    directSellingActive &&
    VAIntegration &&
    loggedUser?.type === User.TYPE.COLLABORATOR;
  const showCV =
    directSellingActive &&
    CVIntegration &&
    loggedUser?.type === User.TYPE.COLLABORATOR;
  const { pricesWithoutVAT } = orderDetails;
  const vatInfo = pricesWithoutVAT
    ? ` (${t('checkout.payment.vatExcl')})`
    : ` (${t('checkout.payment.vatInc')})`;
  const isTaxFree = orderDetails && orderDetails?.tax === 0;

  const { feePercentage, feeSubTotal } = orderDetails?.fee || {};
  const showFee = feeSubTotal > 0;

  const subtotalFormatted = formatPrice(
    orderDetails.subtotal,
    orderDetails.symbol || '€',
  );

  return (
    <div className="total-card-ammount">
      <div className="title">
        <h2>{t('shop.cart.estimation')}</h2>
      </div>
      <hr />
      <div className="items">
        <p>
          {`${t('shop.cart.items')} (${t('checkout.payment.vatExcl')}):`}
          <span className="items-price">{subtotalFormatted}</span>
        </p>

        {orderDetails.discount > 0 && (
          <div
            className={`${showClearCoupon ? 'row coupon-shop-container' : ''}`}
          >
            {showClearCoupon && (
              <div className="col-1 coupon-remove-btn">
                <button
                  type="button"
                  onClick={onClearCoupon}
                  className="remove-coupon"
                >
                  x
                </button>
              </div>
            )}

            <p className={`${showClearCoupon ? 'col-11' : ''}`}>
              {`${t('shop.cart.coupon')}${vatInfo}`}
              <span className="items-price">
                {`- ${formatPrice(
                  orderDetails.discount,
                  orderDetails.symbol || '€',
                )}`}
              </span>
            </p>
          </div>
        )}

        {orderDetails.coinsDiscountTotal > 0 && (
          <div
            className={`${showClearCoins ? 'row coupon-shop-container' : ''}`}
          >
            {showClearCoins && (
              <div className="col-1 coupon-remove-btn">
                <button
                  type="button"
                  onClick={onClearCoins}
                  className="remove-coupon"
                >
                  x
                </button>
              </div>
            )}

            <p className={`${showClearCoupon ? 'col-11' : ''}`}>
              {t('shop.checkout.coupon.coins')}
              <span className="items-price">
                {`- ${formatPrice(
                  orderDetails?.coinsDiscountTotal,
                  orderDetails.symbol || '€',
                )}`}
              </span>
            </p>
          </div>
        )}

        {cartState?.selectedShippingProvider &&
          orderDetails.shippingSubTotal >= 0 && (
            <p>
              {`${t('shop.cart.shipping.provider')} (${t(
                'checkout.payment.vatExcl',
              )}):`}
              <span className="items-price">
                {formatPrice(
                  orderDetails.shippingSubTotal,
                  orderDetails.symbol || '€',
                )}
              </span>
            </p>
          )}

        {orderDetails.shippingDiscountTotal > 0 && (
          <div
            className={`${showClearCoupon ? 'row coupon-shop-container' : ''}`}
          >
            {showClearCoupon && (
              <div className="col-1 coupon-remove-btn">
                <button
                  type="button"
                  onClick={onClearCoupon}
                  className="remove-coupon"
                >
                  x
                </button>
              </div>
            )}

            <p className={`${showClearCoupon ? 'col-11' : ''}`}>
              {`${t('shop.order_discount_shipping_coupon')}${vatInfo}`}
              <span className="items-price">
                {`- ${formatPrice(
                  orderDetails.shippingDiscountTotal,
                  orderDetails.symbol || '€',
                )}`}
              </span>
            </p>
          </div>
        )}

        {orderDetails.userDiscount > 0 &&
          orderDetails.userDiscountPercentage > 0 && (
            <p>
              {`${t('order_discount')} (${
                orderDetails.userDiscountPercentage
              }%) ${vatInfo}`}
              <span className="items-price">
                {`- ${formatPrice(
                  orderDetails.userDiscount,
                  orderDetails.symbol || '€',
                )}`}
              </span>
            </p>
          )}
        {showFee && (
          <p>
            {`${t('order.fee', `${feePercentage}%`, subtotalFormatted)}  (${t(
              'checkout.payment.vatExcl',
            )}):`}
            <span className="items-price">
              {formatPrice(feeSubTotal, orderDetails.symbol || '€')}
            </span>
          </p>
        )}
        <p>
          {t('shop.order.detail.vat')}
          <span className="items-price">
            {orderDetails &&
            orderDetails.tax + orderDetails.shippingSubTotalTax >= 0 ? (
              formatPrice(
                orderDetails.tax + orderDetails.shippingSubTotalTax || 0,
                orderDetails.symbol || '€',
              )
            ) : (
              <span>{t('free')}</span>
            )}
          </span>
        </p>
        {orderDetails?.subtotalExtraTax > 0 && (
          <p>
            {t('order.extraTax')}
            <span className="items-price">
              {formatPrice(
                orderDetails.subtotalExtraTax || 0,
                orderDetails.symbol || '€',
              )}
            </span>
          </p>
        )}
      </div>
      <hr />
      {showVA && (
        <p>
          {t('shop.product.totalVA')}
          <span className="items-price">
            {(orderDetails && normalizeNumber(orderDetails.totalVA, 2)) || 0}
          </span>
        </p>
      )}
      {showCV && (
        <p>
          {t('shop.product.totalCV')}
          <span className="items-price">
            {(orderDetails && normalizeNumber(orderDetails.totalCV, 2)) || 0}
          </span>
        </p>
      )}
      <h3>
        {t('shop.product.total')}
        <span className="items-price">
          {frequency
            ? `${formatPrice(
                orderDetails.total || 0,
                orderDetails.symbol || '€',
              )} / ${frequency}`
            : formatPrice(orderDetails.total || 0, orderDetails.symbol || '€')}
        </span>
      </h3>
      <span className="items-price info-vat">
        {isTaxFree ? t('vatFree') : t('checkout.withVat')}
      </span>
    </div>
  );
}

ShopPriceEstimation.propTypes = {
  t: PropTypes.func.isRequired,
  cartState: PropTypes.shape({
    billingAddress: PropTypes.shape(AddressProp.propType),
    selectedShippingProvider: PropTypes.shape(ShippingProviderProp.propType),
  }),
  orderDetails: PropTypes.shape(OrderPriceProp.propType),
  onClearCoupon: PropTypes.func.isRequired,
  showClearCoupon: PropTypes.bool,
  showClearCoins: PropTypes.bool,
  onClearCoins: PropTypes.func.isRequired,
  directSellingActive: PropTypes.bool.isRequired,
  frequency: PropTypes.string,
};

ShopPriceEstimation.defaultProps = {
  orderDetails: PropTypes.shape(OrderPriceProp.defaultProp),
  showClearCoupon: false,
  showClearCoins: false,
  cartState: {
    billingAddress: {},
  },
  frequency: '',
};

export default translate(ShopPriceEstimation);
